import { useEffect, useRef } from "react";
/**
 * Photographs taken at the table.
 *
 * Both screens that take one work the same way: the camera gives four or five megabytes,
 * and what the desk needs is a few hundred kilobytes it can read a card off. The buyback
 * screen photographs what the shop is buying; the sell screen photographs what it is
 * selling when the thing is not on the system yet.
 */

/** Long enough for the desk to read a card, small enough to send on a booth connection. */
const MAX_SIDE = 1600;
const QUALITY = 0.8;

/** Counted, so each photo taken in this session has a name of its own. */
let taken = 0;

export interface Taken {
  readonly id: string;
  readonly contentType: string;
  /** The picture, base64, as it travels and as it waits in the queue. */
  readonly data: string;
  /** A blob URL, for showing it back on the screen it was taken on. */
  readonly preview: string;
}

/**
 * A photo the size the desk needs, not the size the camera gives. A picture for a tile
 * asks for less: a hundred of them come down to every iPad, over a show's connection.
 */
export async function shrink(file: File, maxSide: number = MAX_SIDE): Promise<Taken> {
  const image = await createImageBitmap(file);
  const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(image.width * scale);
  canvas.height = Math.round(image.height * scale);
  const context = canvas.getContext("2d");
  if (!context) throw new Error("no canvas");
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  image.close();

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", QUALITY));
  if (!blob) throw new Error("no photo");
  const data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.onerror = () => reject(new Error("unreadable"));
    reader.readAsDataURL(blob);
  });
  taken += 1;
  return { id: `photo-${taken}`, contentType: "image/jpeg", data, preview: URL.createObjectURL(blob) };
}

/**
 * The same photo turned a quarter to the right (D, 15 October 2026: "can you allow to rotate
 * photo, when we take photo during trade show, and also during the past day key in").
 */
export async function turned(photo: Taken): Promise<Taken> {
  const image = await createImageBitmap(await (await fetch(photo.preview)).blob());
  const canvas = document.createElement("canvas");
  canvas.width = image.height;
  canvas.height = image.width;
  const context = canvas.getContext("2d");
  if (!context) throw new Error("no canvas");
  context.translate(canvas.width, 0);
  context.rotate(Math.PI / 2);
  context.drawImage(image, 0, 0);
  image.close();
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, "image/jpeg", QUALITY));
  if (!blob) throw new Error("no photo");
  const data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.onerror = () => reject(new Error("unreadable"));
    reader.readAsDataURL(blob);
  });
  URL.revokeObjectURL(photo.preview);
  return { id: photo.id, contentType: "image/jpeg", data, preview: URL.createObjectURL(blob) };
}

/**
 * Take a picture that was copied elsewhere and pasted here with Ctrl+V (D, 12 October 2026:
 * keying in old shows from a chat's photos, "instead of I need to save the image first then
 * upload"). Listens while the window that uses it is open. Pasted text is left alone.
 */
export function usePastedImages(onImages: (files: File[]) => void): void {
  const latest = useRef(onImages);
  latest.current = onImages;
  useEffect(() => {
    function onPaste(event: ClipboardEvent): void {
      const files = [...(event.clipboardData?.items ?? [])]
        .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
        .map((item) => item.getAsFile())
        .filter((file): file is File => file !== null);
      if (files.length === 0) return;
      event.preventDefault();
      latest.current(files);
    }
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);
}
